import {defineStore} from 'pinia';
import type {AgentMessage, CustomerCardData} from "@app/api/admin/ai-api";

export type AiAgentState = 'idle' | 'listening' | 'processing' | 'speaking' | 'error';

interface IAiAgentStore {
  isDrawerOpen: boolean;
  messages: AgentMessage[];
  agentState: AiAgentState;
  error: string | null;
  interimText: string;
  streamingResponse: string;
  streamingUserText: string;
  currentToolName: string | null;
}

export const useAiAgentStore = defineStore('ai-agent', {
  state: (): IAiAgentStore => ({
    isDrawerOpen: false,
    messages: [],
    agentState: 'idle',
    error: null,
    interimText: '',
    streamingResponse: '',
    streamingUserText: '',
    currentToolName: null,
  }),
  getters: {
    getMessages(state): AgentMessage[] {
      return state.messages;
    },
    getAgentState(state): AiAgentState {
      return state.agentState;
    },
    getIsDrawerOpen(state): boolean {
      return state.isDrawerOpen;
    },
  },
  actions: {
    openDrawer() {
      this.messages = [];
      this.agentState = 'idle';
      this.error = null;
      this.interimText = '';
      this.streamingResponse = '';
      this.streamingUserText = '';
      this.currentToolName = null;
      this.isDrawerOpen = true;
    },
    closeDrawer() {
      this.isDrawerOpen = false;
      this.messages = [];
      this.agentState = 'idle';
      this.error = null;
      this.interimText = '';
      this.streamingResponse = '';
      this.streamingUserText = '';
      this.currentToolName = null;
    },
    addUserMessage(content: string) {
      if (!content.trim()) return;
      this.messages.push({role: 'user', content, timestamp: Date.now()});
    },
    addAssistantMessage(content: string) {
      if (!content.trim()) return;
      this.messages.push({role: 'assistant', content, timestamp: Date.now()});
    },
    setAgentState(state: AiAgentState) {
      this.agentState = state;
    },
    setError(error: string | null) {
      this.error = error;
      if (error) this.agentState = 'error';
    },
    setInterimText(text: string) {
      this.interimText = text;
    },
    setStreamingResponse(text: string) {
      this.streamingResponse = text;
    },
    setStreamingUserText(text: string) {
      this.streamingUserText = text;
    },
    setCurrentTool(name: string | null) {
      this.currentToolName = name;
    },
    // Finalize: push accumulated interim/streaming text as messages
    finalizeUserMessage() {
      const text = this.streamingUserText.trim() || this.interimText.trim();
      if (text) {
        this.messages.push({role: 'user', content: text, timestamp: Date.now()});
      }
      this.streamingUserText = '';
      this.interimText = '';
    },
    finalizeAssistantMessage() {
      if (this.streamingResponse.trim()) {
        this.messages.push({role: 'assistant', content: this.streamingResponse.trim(), timestamp: Date.now()});
      }
      this.streamingResponse = '';
    },
    addCustomerCard(card: CustomerCardData) {
      this.messages.push({ role: 'assistant', content: '', customerCard: card, timestamp: Date.now() });
    },
  }
});
